import { useState, useMemo } from "react";
import { readSettings } from "@/hooks/use-settings";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, STANDARD_ROUND, nearestRound } from "./duct-helpers";

const COMMON_VELOCITIES = [
  { label: "Residential Supply (600 fpm)", fpm: 600 },
  { label: "Residential Return (400 fpm)", fpm: 400 },
  { label: "Commercial Supply (1200 fpm)", fpm: 1200 },
  { label: "Commercial Return (800 fpm)", fpm: 800 },
  { label: "Industrial (1500 fpm)", fpm: 1500 },
  { label: "High Velocity (2500 fpm)", fpm: 2500 },
];

export const DuctSizingCalc = () => {
  const defaults = readSettings();
  const [cfm, setCfm] = useState("");
  const [velocityMode, setVelocityMode] = useState("preset");
  const [presetVel, setPresetVel] = useState<string>(defaults.ductVelocity);
  const [customVel, setCustomVel] = useState("");

  const cfmVal = parseFloat(cfm);
  const vel = velocityMode === "preset" ? parseFloat(presetVel) : parseFloat(customVel);

  const areaSqFt = cfmVal > 0 && vel > 0 ? cfmVal / vel : 0;
  const areaSqIn = areaSqFt * 144;
  const diamIn = areaSqIn > 0 ? Math.sqrt((4 * areaSqIn) / Math.PI) : 0;
  const stdRound = diamIn > 0 ? nearestRound(diamIn) : 0;

  const rectSuggestions = useMemo(() => {
    if (areaSqIn <= 0) return [];
    const widths = [6,8,10,12,14,16,18,20,22,24,28,30,32,36,40,48];
    const results: { w: number; h: number; actual: number }[] = [];
    for (const w of widths) {
      const h = Math.ceil(areaSqIn / w);
      if (h >= 4 && h <= 48 && w >= h * 0.25) {
        const roundedH = Math.ceil(h / 2) * 2;
        results.push({ w, h: roundedH, actual: w * roundedH });
      }
    }
    return results.slice(0, 6);
  }, [areaSqIn]);

  const actualVel = stdRound > 0 ? cfmVal / ((Math.PI * (stdRound / 2) ** 2) / 144) : 0;

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Enter CFM and target velocity to calculate required duct size. Based on Q = V × A.
      </p>
      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Airflow (CFM)</label>
        <Input type="number" inputMode="decimal" placeholder="e.g. 400" value={cfm} onChange={(e) => setCfm(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data text-lg" />
      </div>
      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Velocity</label>
        <div className="flex gap-2 mb-2">
          <button onClick={() => setVelocityMode("preset")} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${velocityMode === "preset" ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>Preset</button>
          <button onClick={() => setVelocityMode("custom")} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${velocityMode === "custom" ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>Custom</button>
        </div>
        {velocityMode === "preset" ? (
          <Select value={presetVel} onValueChange={setPresetVel}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>{COMMON_VELOCITIES.map((v) => (<SelectItem key={v.fpm} value={String(v.fpm)}>{v.label}</SelectItem>))}</SelectContent>
          </Select>
        ) : (
          <Input type="number" inputMode="decimal" placeholder="Velocity in FPM" value={customVel} onChange={(e) => setCustomVel(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" />
        )}
      </div>
      {cfmVal > 0 && vel > 0 && diamIn > 0 && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-primary/40 bg-primary/5">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Round Duct (nearest std)</span>
              <span className="font-mono-data text-xl text-primary font-bold">{stdRound}" ⌀</span>
            </div>
          </div>
          <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Calculated Diameter</span><span className="font-mono-data text-foreground font-bold">{fmt(diamIn)}"</span></div></div>
          <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Required Area</span><span className="font-mono-data text-foreground font-bold">{fmt(areaSqIn)} in²</span></div></div>
          <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Actual Velocity ({stdRound}" duct)</span><span className={`font-mono-data font-bold ${actualVel > 1500 ? "text-destructive" : "text-foreground"}`}>{fmt(actualVel, 0)} fpm</span></div></div>
          {actualVel > 2000 && <p className="text-xs text-destructive font-semibold">⚠️ Velocity exceeds 2000 fpm — may cause excessive noise</p>}
          {rectSuggestions.length > 0 && (
            <>
              <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">Rectangular Equivalents</h3>
              <div className="grid grid-cols-2 gap-2">
                {rectSuggestions.map((r, i) => (
                  <div key={i} className="trade-card text-center">
                    <span className="font-mono-data text-sm font-bold text-foreground">{r.w}" × {r.h}"</span>
                    <span className="text-[10px] text-muted-foreground block">{r.actual} in²</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const EquivalentDuct = () => {
  const [mode, setMode] = useState<"rect-to-round" | "round-to-rect">("rect-to-round");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [roundDiam, setRoundDiam] = useState("");
  const [targetRatio, setTargetRatio] = useState("2");

  const rd = parseFloat(roundDiam);
  const ratio = parseFloat(targetRatio) || 2;
  const roundAreaR = rd > 0 ? Math.PI * (rd / 2) ** 2 : 0;

  const rectOptions = useMemo(() => {
    if (rd <= 0) return [];
    const de = rd;
    const widths = [6,8,10,12,14,16,18,20,22,24,28,30,32,36,40,48];
    const results: { w: number; h: number; deCalc: number }[] = [];
    for (const w of widths) {
      for (let h = 4; h <= 48; h += 2) {
        const calc = 1.3 * Math.pow(w * h, 0.625) / Math.pow(w + h, 0.25);
        if (Math.abs(calc - de) < 1.0 && w / h <= 4 && w / h >= 0.25) {
          results.push({ w, h, deCalc: calc });
        }
      }
    }
    results.sort((a, b) => Math.abs(a.w / a.h - ratio) - Math.abs(b.w / b.h - ratio));
    const seen = new Set<string>();
    return results.filter(r => {
      const key = `${Math.min(r.w, r.h)}x${Math.max(r.w, r.h)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 8);
  }, [rd, ratio]);

  const w = parseFloat(width);
  const h = parseFloat(height);
  const de = w > 0 && h > 0 ? 1.3 * Math.pow(w * h, 0.625) / Math.pow(w + h, 0.25) : 0;
  const std = de > 0 ? nearestRound(de) : 0;
  const rectArea = w * h;
  const roundArea = std > 0 ? Math.PI * (std / 2) ** 2 : 0;

  const modeButtons = (
    <div className="flex gap-2 mb-2">
      <button onClick={() => setMode("rect-to-round")} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${mode === "rect-to-round" ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>Rect → Round</button>
      <button onClick={() => setMode("round-to-rect")} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${mode === "round-to-rect" ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>Round → Rect</button>
    </div>
  );

  if (mode === "rect-to-round") {
    return (
      <div className="space-y-4">
        {modeButtons}
        <p className="text-[11px] text-muted-foreground leading-relaxed">ASHRAE equivalent diameter: De = 1.3 × (a×b)^0.625 / (a+b)^0.25</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Width (in)</label><Input type="number" inputMode="decimal" placeholder="e.g. 24" value={width} onChange={(e) => setWidth(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" /></div>
          <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Height (in)</label><Input type="number" inputMode="decimal" placeholder="e.g. 12" value={height} onChange={(e) => setHeight(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" /></div>
        </div>
        {de > 0 && (
          <div className="space-y-2 animate-fade-in">
            <div className="trade-card border-primary/40 bg-primary/5"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Equivalent Round</span><span className="font-mono-data text-xl text-primary font-bold">{std}" ⌀</span></div></div>
            <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Exact Equivalent</span><span className="font-mono-data text-foreground font-bold">{fmt(de)}" ⌀</span></div></div>
            <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Rectangular Area</span><span className="font-mono-data text-foreground font-bold">{fmt(rectArea, 0)} in²</span></div></div>
            <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Round Area ({std}")</span><span className="font-mono-data text-foreground font-bold">{fmt(roundArea, 0)} in²</span></div></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {modeButtons}
      <p className="text-[11px] text-muted-foreground leading-relaxed">Find rectangular duct sizes equivalent to a given round duct diameter.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Round Diameter (in)</label>
          <Select value={roundDiam} onValueChange={setRoundDiam}><SelectTrigger className="bg-card border-border font-mono-data"><SelectValue placeholder="Select size" /></SelectTrigger><SelectContent>{STANDARD_ROUND.map((s) => (<SelectItem key={s} value={String(s)}>{s}"</SelectItem>))}</SelectContent></Select>
        </div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Max Aspect Ratio</label>
          <Select value={targetRatio} onValueChange={setTargetRatio}><SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="2">2:1</SelectItem><SelectItem value="3">3:1</SelectItem><SelectItem value="4">4:1</SelectItem></SelectContent></Select>
        </div>
      </div>
      {rd > 0 && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Round Area ({rd}")</span><span className="font-mono-data text-foreground font-bold">{fmt(roundAreaR, 0)} in²</span></div></div>
          {rectOptions.length > 0 ? (
            <>
              <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Equivalent Rectangular Sizes</h3>
              <div className="grid grid-cols-2 gap-2">
                {rectOptions.map((r, i) => (
                  <div key={i} className={`trade-card text-center ${i === 0 ? "border-primary/40 bg-primary/5" : ""}`}>
                    <span className={`font-mono-data text-sm font-bold ${i === 0 ? "text-primary" : "text-foreground"}`}>{r.w}" × {r.h}"</span>
                    <span className="text-[10px] text-muted-foreground block">De = {fmt(r.deCalc)}"</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No rectangular equivalents found for this size.</p>
          )}
        </div>
      )}
    </div>
  );
};

export const CfmCalc = () => {
  const [solveFor, setSolveFor] = useState<"cfm" | "velocity" | "area">("cfm");
  const [cfmIn, setCfmIn] = useState("");
  const [velIn, setVelIn] = useState("");
  const [ductShape, setDuctShape] = useState<"round" | "rect">("round");
  const [roundD, setRoundD] = useState("");
  const [rectW, setRectW] = useState("");
  const [rectH, setRectH] = useState("");

  const getArea = (): number => {
    if (ductShape === "round") { const d = parseFloat(roundD); return d > 0 ? (Math.PI * (d / 2) ** 2) / 144 : 0; }
    const w = parseFloat(rectW); const h = parseFloat(rectH);
    return w > 0 && h > 0 ? (w * h) / 144 : 0;
  };

  const area = getArea();
  const cfmVal = parseFloat(cfmIn);
  const velVal = parseFloat(velIn);

  let resultLabel = "", resultValue = "", resultUnit = "";

  if (solveFor === "cfm" && velVal > 0 && area > 0) { resultLabel = "Airflow"; resultValue = fmt(velVal * area, 0); resultUnit = "CFM"; }
  else if (solveFor === "velocity" && cfmVal > 0 && area > 0) { resultLabel = "Velocity"; resultValue = fmt(cfmVal / area, 0); resultUnit = "FPM"; }
  else if (solveFor === "area" && cfmVal > 0 && velVal > 0) { const a = cfmVal / velVal; resultLabel = "Required Area"; resultValue = fmt(a * 144, 1); resultUnit = "in²"; }

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">Q = V × A — Solve for CFM, velocity, or area.</p>
      <div className="flex gap-2">
        {(["cfm", "velocity", "area"] as const).map(s => (
          <button key={s} onClick={() => setSolveFor(s)} className={`text-xs px-3 py-1.5 rounded-md border transition-colors capitalize ${solveFor === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>{s === "cfm" ? "CFM" : s === "velocity" ? "Velocity" : "Area"}</button>
        ))}
      </div>
      {solveFor !== "cfm" && (
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Airflow (CFM)</label>
          <Input type="number" inputMode="decimal" placeholder="e.g. 800" value={cfmIn} onChange={(e) => setCfmIn(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" /></div>
      )}
      {solveFor !== "velocity" && (
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Velocity (FPM)</label>
          <Input type="number" inputMode="decimal" placeholder="e.g. 900" value={velIn} onChange={(e) => setVelIn(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" /></div>
      )}
      {solveFor !== "area" && (
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Duct Shape</label>
          <div className="flex gap-2 mb-2">
            {(["round", "rect"] as const).map(s => (
              <button key={s} onClick={() => setDuctShape(s)} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${ductShape === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>{s === "round" ? "Round" : "Rectangular"}</button>
            ))}
          </div>
          {ductShape === "round" ? (
            <Input type="number" inputMode="decimal" placeholder="Diameter (in)" value={roundD} onChange={(e) => setRoundD(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" inputMode="decimal" placeholder="Width (in)" value={rectW} onChange={(e) => setRectW(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" />
              <Input type="number" inputMode="decimal" placeholder="Height (in)" value={rectH} onChange={(e) => setRectH(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" />
            </div>
          )}
        </div>
      )}
      {resultValue && (
        <div className="trade-card border-primary/40 bg-primary/5 animate-fade-in">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">{resultLabel}</span>
            <span className="font-mono-data text-xl text-primary font-bold">{resultValue} <span className="text-xs font-normal text-muted-foreground">{resultUnit}</span></span>
          </div>
        </div>
      )}
      {solveFor === "velocity" && resultValue && parseFloat(resultValue.replace(",", "")) > 2000 && (
        <p className="text-xs text-destructive font-semibold">⚠️ Velocity exceeds 2000 fpm — may cause noise issues</p>
      )}
    </div>
  );
};

const NC_LIMITS: { space: string; maxFpm: number; nc: string }[] = [
  { space: "Broadcast Studio", maxFpm: 300, nc: "15–20" },
  { space: "Concert Hall", maxFpm: 350, nc: "15–20" },
  { space: "Hospital / Bedroom", maxFpm: 500, nc: "25–30" },
  { space: "Private Office", maxFpm: 600, nc: "30–35" },
  { space: "Classroom", maxFpm: 600, nc: "25–30" },
  { space: "Conference Room", maxFpm: 700, nc: "25–30" },
  { space: "Library", maxFpm: 600, nc: "30–35" },
  { space: "Open Plan Office", maxFpm: 800, nc: "35–40" },
  { space: "Restaurant", maxFpm: 900, nc: "40–45" },
  { space: "Retail / Commercial", maxFpm: 1000, nc: "40–45" },
  { space: "Light Manufacturing", maxFpm: 1200, nc: "45–55" },
  { space: "Industrial / Warehouse", maxFpm: 1500, nc: "50–60" },
];

export const VelocityNoiseCalc = () => {
  const [cfm, setCfm] = useState("");
  const [shape, setShape] = useState<"round" | "rect">("round");
  const [diam, setDiam] = useState("");
  const [rw, setRw] = useState("");
  const [rh, setRh] = useState("");

  const cfmVal = parseFloat(cfm);
  const area = shape === "round"
    ? (() => { const d = parseFloat(diam); return d > 0 ? (Math.PI * (d / 2) ** 2) / 144 : 0; })()
    : (() => { const w = parseFloat(rw); const h = parseFloat(rh); return w > 0 && h > 0 ? (w * h) / 144 : 0; })();
  const velocity = cfmVal > 0 && area > 0 ? cfmVal / area : 0;

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">Calculate air velocity from CFM & duct size. Checks against ASHRAE recommended NC levels by space type.</p>
      <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Airflow (CFM)</label><Input type="number" inputMode="decimal" placeholder="e.g. 800" value={cfm} onChange={(e) => setCfm(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data text-lg" /></div>
      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Duct Shape</label>
        <div className="flex gap-2 mb-2">
          {(["round", "rect"] as const).map(s => (<button key={s} onClick={() => setShape(s)} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${shape === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>{s === "round" ? "Round" : "Rectangular"}</button>))}
        </div>
        {shape === "round" ? (
          <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Diameter (in)</label>
            <Select value={diam} onValueChange={setDiam}><SelectTrigger className="bg-card border-border font-mono-data"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{STANDARD_ROUND.map(s => <SelectItem key={s} value={String(s)}>{s}"</SelectItem>)}</SelectContent></Select></div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Width (in)</label><Input type="number" inputMode="decimal" placeholder="24" value={rw} onChange={(e) => setRw(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" /></div>
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Height (in)</label><Input type="number" inputMode="decimal" placeholder="12" value={rh} onChange={(e) => setRh(e.target.value)} className="bg-secondary text-foreground border-border font-mono-data" /></div>
          </div>
        )}
      </div>
      {velocity > 0 && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-primary/40 bg-primary/5"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Air Velocity</span><span className={`font-mono-data text-xl font-bold ${velocity > 1500 ? "text-destructive" : "text-primary"}`}>{fmt(velocity, 0)} fpm</span></div></div>
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">NC Rating by Space Type</h3>
          <div className="space-y-1">
            {NC_LIMITS.map((n) => {
              const ok = velocity <= n.maxFpm;
              return (
                <div key={n.space} className={`trade-card py-2 flex justify-between items-center ${!ok ? "border-destructive/30 bg-destructive/5" : ""}`}>
                  <div><span className="text-xs font-medium text-foreground">{n.space}</span><span className="text-[10px] text-muted-foreground block">NC {n.nc} · Max {n.maxFpm} fpm</span></div>
                  <span className={`text-xs font-bold ${ok ? "text-green-500" : "text-destructive"}`}>{ok ? "✓ OK" : "✗ LOUD"}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
